import { post, get } from '../api.js';
import { deriveAndStoreKeys, getAuthKeyHex, getEncKey, unwrapAndStoreKeysFromPrf, migrateToMasterKey, rewrapItemKeys, applyMigratedKeys, needsParamUpgrade, needsAadUpgrade, upgradeArgonParams, wipe } from '../crypto.js';
import { isTauri } from '../backend.js';
import { navigate } from '../router.js';
import { isWebAuthnAvailable, authenticate } from '../webauthn.js';
import { lucideIcons, esc } from '../app.js';
import { isSessionExpired, clearSessionExpired, setEmail, setToken, setRememberedEmail } from '../auth-store.js';

export function renderLogin(container) {
  const hashParts = window.location.hash.split('?');
  const params = new URLSearchParams(hashParts[1] || '');
  const prefillEmail = params.get('email') || '';

  container.innerHTML = `
    <div class="login-card">
      <div class="login-back" id="login-back"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Back to drop</div>
      <div class="logo">
        <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-login" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-login)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-login)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-login)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </div>
      <div id="login-msg"></div>
      <div class="login-step active" id="step-email">
        <div class="field">
          <label>Email address</label>
          <input type="email" id="login-email" placeholder="you@example.com">
        </div>
        <button class="btn btn-primary" id="email-continue-btn">Continue</button>
      </div>
      <div class="login-step" id="step-auth"></div>
    </div>
  `;
  lucideIcons();

  // Show session expired message if redirected from a 401
  if (isSessionExpired()) {
    clearSessionExpired();
    document.getElementById('login-msg').innerHTML = '<div class="msg msg-warning">Your session has expired. Please sign in again.</div>';
  }

  if (prefillEmail) {
    document.getElementById('login-email').value = prefillEmail;
  }

  document.getElementById('login-back').onclick = () => navigate('#/drop');

  const autoPasskey = params.get('autoPasskey') === '1';

  document.getElementById('email-continue-btn').onclick = () => {
    const email = document.getElementById('login-email').value;
    if (!email) return;
    showAuthStep(email);
  };

  // Auto-trigger login flow when redirected from refresh with a known email
  if (prefillEmail && autoPasskey) {
    document.getElementById('email-continue-btn').click();
  }

  // Allow Enter key on email field
  document.getElementById('login-email').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.getElementById('email-continue-btn').click();
    }
  });

  function showAuthStep(email) {
    document.getElementById('step-email').classList.remove('active');
    const stepAuth = document.getElementById('step-auth');
    stepAuth.classList.add('active');

    const hasWebAuthn = isWebAuthnAvailable();

    let html = `
      <p style="margin-bottom:1rem;color:var(--text-secondary)">Signing in as <strong style="color:var(--text)">${esc(email)}</strong>.</p>
      <div id="auth-msg"></div>
    `;

    if (hasWebAuthn) {
      html += '<button class="btn btn-primary" id="passkey-btn" style="width:100%"><i data-lucide="fingerprint" style="width:18px;height:18px"></i> Unlock with Face ID</button>';
      html += '<div class="divider">or</div>';
    }

    html += `
      <div id="password-section">
        <div class="field">
          <label>Master password</label>
          <input type="password" id="login-password" placeholder="Enter your master password">
        </div>
        <button class="btn btn-${hasWebAuthn ? 'secondary' : 'primary'}" id="password-signin-btn">Unlock with Password</button>
      </div>
    `;

    html += `<div class="divider" style="margin:16px 0 12px">or</div>`;
    html += `<button class="btn btn-ghost" id="magic-link-btn" style="width:100%"><i data-lucide="mail" style="width:16px;height:16px"></i> Send sign-in link</button>`;
    html += `<p style="text-align:center;font-size:12px;color:var(--text-secondary);margin-top:4px">Works for existing and new accounts</p>`;
    html += `<div style="text-align:center;margin-top:12px"><span class="login-back" id="change-email-btn">Back</span></div>`;

    stepAuth.innerHTML = html;
    lucideIcons();
    setupChangeLink(email);

    document.getElementById('magic-link-btn').onclick = () => {
      document.getElementById('auth-msg').innerHTML = '<div class="msg msg-info">Sending sign-in link...</div>';
      sendMagicLink(email);
    };

    // Store email for sidebar + persist for passkey auto-login on refresh
    setEmail(email);
    setRememberedEmail(email);

    const pwBtn = document.getElementById('password-signin-btn');
    const pwInput = document.getElementById('login-password');

    if (!autoPasskey) {
      pwInput.focus();
    }

    pwInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); pwBtn.click(); }
    });

    pwBtn.onclick = async () => {
      const authMsg = document.getElementById('auth-msg');
      const password = pwInput.value;
      pwBtn.disabled = true;
      authMsg.innerHTML = '';
      try {
        const params = await post('/auth/client-params', { email });
        deriveAndStoreKeys(password, params.client_salt, null, null);
        const authKeyHex = getAuthKeyHex();
        const auth = await post('/auth/login', { email, auth_key: authKeyHex });
        setToken(auth.token);
        const me = await get('/auth/me');
        deriveAndStoreKeys(
          password,
          me.client_salt,
          me.encrypted_private_key,
          me.public_key,
          me.encrypted_master_key,
          me.encrypted_master_key_format_version,
          me.encrypted_private_key_format_version,
        );

        if (!me.encrypted_master_key) {
          authMsg.innerHTML = '<div class="msg msg-info">Upgrading vault security...</div>';
          const migration = migrateToMasterKey(password, me.client_salt);
          try {
            const items = await get('/items');
            const rewrapped = rewrapItemKeys(items, migration.oldEncKey, migration.newEncKey);
            await post('/auth/migrate-keys', {
              encrypted_master_key: migration.encryptedMasterKey,
              encrypted_private_key: migration.encryptedPrivateKey,
              items: rewrapped,
            });
            applyMigratedKeys(migration.masterKey, migration.newEncKey);
          } finally {
            // oldEncKey is a snapshot of the pre-migration encKey; once
            // applyMigratedKeys swaps in the new keys it's no longer needed
            // and shouldn't linger on the heap. (#136)
            wipe(migration.oldEncKey);
          }
        } else if (needsParamUpgrade()) {
          authMsg.innerHTML = '<div class="msg msg-info">Upgrading encryption parameters...</div>';
          const upgraded = upgradeArgonParams(password, me.client_salt);
          await post('/auth/upgrade-params', upgraded);
        } else if (needsAadUpgrade()) {
          authMsg.innerHTML = '<div class="msg msg-info">Upgrading encryption format...</div>';
          const upgraded = upgradeArgonParams(password, me.client_salt);
          await post('/auth/upgrade-params', { ...upgraded, reason: 'aad_upgrade' });
        }

        // Bridge enc_key to Tauri backend for USB operations
        if (isTauri) {
          const encKeyCopy = getEncKey();
          try {
            await window.__TAURI_INTERNALS__.invoke('set_enc_key', { encKey: Array.from(encKeyCopy) });
          } catch (e) {
            console.warn('Failed to set enc_key in Tauri backend:', e);
          } finally {
            wipe(encKeyCopy);
          }
        }

        navigate('#/items');
      } catch (err) {
        const isAuthError = err.message === 'Session expired' || err.message === 'Unauthorized';
        if (isAuthError) {
          authMsg.innerHTML = `<div class="msg msg-error">Incorrect password or unknown account. <a href="#/recover" style="color:var(--accent)">Forgot your password?</a></div>`;
        } else {
          authMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
        pwBtn.disabled = false;
      }
    };

    if (hasWebAuthn) {
      const passkeyHandler = async () => {
        const authMsg = document.getElementById('auth-msg');
        const btn = document.getElementById('passkey-btn');
        btn.disabled = true;
        authMsg.innerHTML = '';
        try {
          const result = await authenticate(email);
          setToken(result.token);
          setRememberedEmail(email);
          if (result.has_prf && result.prf_output && result.prf_wrapped_master_key) {
            unwrapAndStoreKeysFromPrf(
              result.prf_output,
              result.prf_wrapped_master_key,
              result.encrypted_private_key,
              result.public_key,
              result.encrypted_private_key_format_version,
            );
            if (isTauri) {
              const encKeyCopy = getEncKey();
              try { await window.__TAURI_INTERNALS__.invoke('set_enc_key', { encKey: Array.from(encKeyCopy) }); } catch (e) {} finally { wipe(encKeyCopy); }
            }
            navigate('#/items');
          } else if (!result.has_prf && !result.prf_output) {
            // Passkey has no PRF key — redirect to unlock which will
            // show the appropriate UI (passkey retry or password form)
            navigate('#/unlock');
          } else {
            // PRF was expected but prf_output missing — authenticator
            // didn't return PRF data (e.g. 1Password PRF flake)
            authMsg.innerHTML = '<div class="msg msg-error">Passwordless unlock failed — your authenticator did not return encryption data. Try again, or <a href="#/unlock" style="color:var(--accent)">unlock another way</a>.</div>';
            btn.disabled = false;
          }
        } catch (err) {
          if (err.name === 'NotAllowedError') {
            authMsg.innerHTML = '<div class="msg msg-error">Passkey operation was cancelled.</div>';
          } else {
            authMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
          }
          btn.disabled = false;
        }
      };
      document.getElementById('passkey-btn').onclick = passkeyHandler;

      // Auto-focus passkey button when redirected with autoPasskey flag.
      // We cannot call passkeyHandler() programmatically here — browsers
      // require a genuine user gesture (transient activation) for
      // navigator.credentials.get(), and a scripted click from page-load
      // context does not satisfy that requirement.
      if (autoPasskey) {
        document.getElementById('passkey-btn').focus();
      }
    }
  }

  function setupChangeLink(email) {
    const changeBtn = document.getElementById('change-email-btn');
    if (changeBtn) {
      changeBtn.onclick = () => {
        document.getElementById('step-auth').classList.remove('active');
        document.getElementById('step-email').classList.add('active');
        document.getElementById('login-msg').innerHTML = '';
        document.getElementById('login-email').value = email;
        document.getElementById('email-continue-btn').disabled = false;
      };
    }
  }

  async function sendMagicLink(email) {
    const authMsg = document.getElementById('auth-msg');
    try {
      await post('/auth/magic-link', { email });
      authMsg.innerHTML = '<div class="msg msg-success">Check your email for a sign-in link.</div>';
    } catch (err) {
      authMsg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    }
  }
}
